import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ChevronLeft,
  ChevronRight,
  Download,
  ExternalLink,
  FileQuestion,
  Maximize2,
  Minimize2,
  X,
} from 'lucide-react';
import type { Nodo, TipoVista } from '@doc/shared';
import { ETIQUETA_TIPO_VISTA, necesitaConversion, tipoDeVista } from '@doc/shared';
import { api, urlArchivo } from '../../lib/api.js';
import { VistaPDF } from './VistaPDF.js';
import { fecha, iconoDe, tamano } from '../../lib/formato.js';

interface Props {
  nodo: Nodo;
  /** Los demás archivos de la carpeta, para pasar de uno a otro sin salir. */
  hermanos: Nodo[];
  alNavegar(nodo: Nodo): void;
  alDescargar(nodo: Nodo): void;
  alCerrar(): void;
  /**
   * De dónde salen los bytes. Por defecto, las rutas con sesión; la página de
   * un enlace compartido pasa las suyas, que van firmadas por el token del
   * enlace. Es lo único que cambia entre los dos usos del visor.
   */
  construirUrl?(nodoId: string, modo: 'vista' | 'descargar', version?: number): string;
  rutaPreparar?(nodoId: string): string;
  /** El enlace puede prohibir la descarga; entonces no se ofrece. */
  permiteDescarga?: boolean;
}

/**
 * Visor de documentos.
 *
 * Word, Excel y PowerPoint no los dibuja ningún navegador: para esos, el
 * servidor genera un PDF con LibreOffice y acá se muestra ese PDF. El resto
 * —imágenes, PDF, audio, video y texto— se sirve tal cual.
 */
export function VisorDocumento({
  nodo,
  hermanos,
  alNavegar,
  alDescargar,
  alCerrar,
  construirUrl = urlArchivo,
  rutaPreparar = (id) => `/nodos/${id}/vista/preparar`,
  permiteDescarga = true,
}: Props) {
  const tipo = tipoDeVista(nodo.mime, nodo.nombre);
  const [ajustar, setAjustar] = useState(true);

  const visibles = useMemo(() => hermanos.filter((h) => h.tipo === 'ARCHIVO'), [hermanos]);
  const indice = visibles.findIndex((h) => h.id === nodo.id);
  const anterior = indice > 0 ? visibles[indice - 1]! : null;
  const siguiente = indice >= 0 && indice < visibles.length - 1 ? visibles[indice + 1]! : null;

  // La conversión a PDF tarda segundos la primera vez. Se pide antes de
  // apuntar el iframe: así se puede mostrar "Preparando…" y, sobre todo, el
  // motivo cuando falla — dentro del iframe el error sería una página en blanco.
  const preparacion = useQuery({
    queryKey: ['vista', nodo.id, nodo.version],
    queryFn: () => api<{ tipo: TipoVista; listo: boolean }>(rutaPreparar(nodo.id)),
    enabled: necesitaConversion(tipo),
    retry: false,
    staleTime: 5 * 60_000,
  });

  const irA = useCallback(
    (destino: Nodo | null) => {
      if (destino) alNavegar(destino);
    },
    [alNavegar],
  );

  useEffect(() => {
    const alTeclear = (e: KeyboardEvent) => {
      if (e.key === 'Escape') alCerrar();
      else if (e.key === 'ArrowLeft') irA(anterior);
      else if (e.key === 'ArrowRight') irA(siguiente);
    };
    document.addEventListener('keydown', alTeclear);
    return () => document.removeEventListener('keydown', alTeclear);
  }, [alCerrar, irA, anterior, siguiente]);

  const { Icono, clase } = iconoDe(nodo);

  return (
    <div className="visor" onMouseDown={(e) => e.target === e.currentTarget && alCerrar()}>
      <header className="visor__cabecera">
        <span className={`icono-tipo ${clase}`}>
          <Icono size={16} />
        </span>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div className="visor__nombre">{nodo.nombre}</div>
          <div className="visor__meta">
            {ETIQUETA_TIPO_VISTA[tipo]} · {tamano(nodo.bytes)} · v{nodo.version} · {fecha(nodo.actualizadoEn)}
          </div>
        </div>

        {tipo === 'IMAGEN' && (
          <button className="btn btn--fantasma btn--icono" title={ajustar ? 'Tamaño real' : 'Ajustar a la ventana'} onClick={() => setAjustar((v) => !v)}>
            {ajustar ? <Maximize2 size={16} /> : <Minimize2 size={16} />}
          </button>
        )}
        <a
          className="btn btn--fantasma btn--icono"
          href={construirUrl(nodo.id, 'vista')}
          target="_blank"
          rel="noreferrer"
          title="Abrir en una pestaña nueva"
        >
          <ExternalLink size={16} />
        </a>
        {permiteDescarga && (
          <button className="btn" onClick={() => alDescargar(nodo)}>
            <Download size={14} /> Descargar
          </button>
        )}
        <button className="btn btn--fantasma btn--icono" onClick={alCerrar} title="Cerrar (Esc)">
          <X size={17} />
        </button>
      </header>

      <div className="visor__cuerpo">
        <button
          className="visor__flecha"
          disabled={!anterior}
          onClick={() => irA(anterior)}
          title={anterior ? anterior.nombre : 'Es el primero'}
        >
          <ChevronLeft size={22} />
        </button>

        <div className="visor__lienzo" onMouseDown={(e) => e.stopPropagation()}>
          <Contenido
            nodo={nodo}
            tipo={tipo}
            ajustar={ajustar}
            preparacion={preparacion}
            alDescargar={permiteDescarga ? alDescargar : undefined}
            construirUrl={construirUrl}
          />
        </div>

        <button
          className="visor__flecha"
          disabled={!siguiente}
          onClick={() => irA(siguiente)}
          title={siguiente ? siguiente.nombre : 'Es el último'}
        >
          <ChevronRight size={22} />
        </button>
      </div>

      {visibles.length > 1 && indice >= 0 && (
        <footer className="visor__pie">
          {indice + 1} de {visibles.length} · usa ← y → para moverte
        </footer>
      )}
    </div>
  );
}

interface PropsContenido {
  nodo: Nodo;
  tipo: TipoVista;
  ajustar: boolean;
  preparacion: ReturnType<typeof useQuery<{ tipo: TipoVista; listo: boolean }>>;
  /** Sin función de descarga, el visor no ofrece bajarse el archivo. */
  alDescargar?(nodo: Nodo): void;
  construirUrl(nodoId: string, modo: 'vista' | 'descargar', version?: number): string;
}

function Contenido({ nodo, tipo, ajustar, preparacion, alDescargar, construirUrl }: PropsContenido) {
  const [errorPdf, setErrorPdf] = useState<string | null>(null);
  useEffect(() => setErrorPdf(null), [nodo.id, nodo.version]);

  if (tipo === 'IMAGEN') {
    return (
      <img
        className={`visor__imagen${ajustar ? '' : ' real'}`}
        src={construirUrl(nodo.id, 'vista')}
        alt={nodo.nombre}
      />
    );
  }

  if (tipo === 'AUDIO') {
    return (
      <div className="visor__medio">
        <audio controls autoPlay={false} src={construirUrl(nodo.id, 'vista')} style={{ width: 'min(560px, 90%)' }} />
      </div>
    );
  }

  if (tipo === 'VIDEO') {
    return (
      <video className="visor__video" controls src={construirUrl(nodo.id, 'vista')} />
    );
  }

  if (tipo === 'TEXTO') return <VistaTexto nodo={nodo} url={construirUrl(nodo.id, 'vista')} />;

  if (tipo === 'PDF' || necesitaConversion(tipo)) {
    if (necesitaConversion(tipo)) {
      if (preparacion.isLoading) {
        return (
          <div className="visor__aviso">
            <div className="cargando">Preparando la vista previa…</div>
            <p className="dim" style={{ fontSize: 12 }}>
              El documento se convierte a PDF en el servidor. La primera vez tarda unos segundos; después queda
              guardado.
            </p>
          </div>
        );
      }
      if (preparacion.isError) {
        return (
          <div className="visor__aviso">
            <FileQuestion size={40} className="dim" />
            <p>{preparacion.error instanceof Error ? preparacion.error.message : 'No se pudo preparar la vista previa'}</p>
            {alDescargar && (
              <button className="btn btn--primario" onClick={() => alDescargar(nodo)}>
                <Download size={14} /> Descargar el archivo
              </button>
            )}
          </div>
        );
      }
    }
    if (errorPdf) {
      return (
        <div className="visor__aviso">
          <FileQuestion size={40} className="dim" />
          <p>{errorPdf}</p>
          {alDescargar && (
            <button className="btn btn--primario" onClick={() => alDescargar(nodo)}>
              <Download size={14} /> Descargar el archivo
            </button>
          )}
        </div>
      );
    }
    return <VistaPDF url={construirUrl(nodo.id, 'vista')} alFallar={setErrorPdf} />;
  }

  return (
    <div className="visor__aviso">
      <FileQuestion size={40} className="dim" />
      <p>Este tipo de archivo no se puede previsualizar.</p>
      {alDescargar && (
        <button className="btn btn--primario" onClick={() => alDescargar(nodo)}>
          <Download size={14} /> Descargar el archivo
        </button>
      )}
    </div>
  );
}

/**
 * Texto plano y CSV.
 *
 * El contenido se pinta como texto, nunca como HTML: un archivo subido por un
 * usuario es dato, no marcado. El CSV se arma como tabla porque en crudo, con
 * cuarenta columnas, no se entiende nada.
 */
function VistaTexto({ nodo, url }: { nodo: Nodo; url: string }) {
  const [texto, setTexto] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let vigente = true;
    setTexto(null);
    setError(null);
    fetch(url)
      .then((r) => (r.ok ? r.text() : Promise.reject(new Error('No se pudo leer el archivo'))))
      // Un .log de 40 MB congelaría la pestaña; se muestra el principio.
      .then((t) => vigente && setTexto(t.slice(0, 400_000)))
      .catch((e) => vigente && setError(e instanceof Error ? e.message : 'Error al leer'));
    return () => {
      vigente = false;
    };
  }, [url]);

  if (error) return <div className="visor__aviso">{error}</div>;
  if (texto === null) return <div className="cargando">Cargando…</div>;

  const esTabla = nodo.mime === 'text/csv' || nodo.mime === 'text/tab-separated-values';
  if (esTabla) {
    const separador = nodo.mime === 'text/tab-separated-values' ? '\t' : ',';
    const filas = texto.split(/\r?\n/).filter((l) => l.length > 0).slice(0, 2000).map((l) => partir(l, separador));
    const [cabecera, ...cuerpo] = filas;
    return (
      <div className="visor__tabla">
        <table className="tabla">
          {cabecera && (
            <thead>
              <tr>
                {cabecera.map((celda, i) => (
                  <th key={i}>{celda}</th>
                ))}
              </tr>
            </thead>
          )}
          <tbody>
            {cuerpo.map((fila, i) => (
              <tr key={i}>
                {fila.map((celda, j) => (
                  <td key={j}>{celda}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return <pre className="visor__texto">{texto}</pre>;
}

/** Separador de CSV que respeta las comillas: "Santiago, Chile" es una celda. */
function partir(linea: string, separador: string): string[] {
  const celdas: string[] = [];
  let actual = '';
  let entreComillas = false;
  for (let i = 0; i < linea.length; i++) {
    const c = linea[i]!;
    if (c === '"') {
      if (entreComillas && linea[i + 1] === '"') {
        actual += '"';
        i++;
      } else entreComillas = !entreComillas;
    } else if (c === separador && !entreComillas) {
      celdas.push(actual);
      actual = '';
    } else actual += c;
  }
  celdas.push(actual);
  return celdas;
}
